import { useMemo, useState, type Key } from 'react';
import { Alert, Button, Collapse, Empty, Spin, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { JobSceneSetNode, SceneSetVerdict } from '@apis/simulation';
import './SceneSetComparison.css';

type Row = Omit<JobSceneSetNode, 'children'> & { children?: Row[] };

const toRows = (nodes: JobSceneSetNode[]): Row[] => nodes.map((node) => ({
  ...node,
  children: node.children.length ? toRows(node.children) : undefined,
}));

const verdictColumns = [
  ['passCount', 'pass', '通过'],
  ['failCount', 'fail', '不通过'],
  ['errorCount', 'error', '异常'],
  ['pendingCount', 'pending', '未完成'],
] as const;

interface Props {
  nodes: JobSceneSetNode[] | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onSelect: (nodeId: number, verdict: SceneSetVerdict) => void;
  selectedNodeId?: number;
  selectedVerdict?: SceneSetVerdict;
}

export default function SceneSetStats({ nodes, loading, error, onRetry, onSelect, selectedNodeId, selectedVerdict }: Props) {
  const [open, setOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<readonly Key[]>([]);
  const rows = useMemo(() => toRows(nodes ?? []), [nodes]);
  const columns: ColumnsType<Row> = [
    { title: '场景集', dataIndex: 'name', width: 260 },
    { title: '场景数', dataIndex: 'total', width: 90, align: 'right' },
    {
      title: '通过率', dataIndex: 'passRate', width: 100, align: 'right',
      render: (rate: number | null, row) => (
        <Tooltip title={`通过 ${row.passCount} / 场景 ${row.total}`}>
          <span>{rate == null ? '—' : `${rate.toFixed(2)}%`}</span>
        </Tooltip>
      ),
    },
    ...verdictColumns.map(([field, verdict, label]) => ({
      title: label, key: field, width: 90, align: 'right' as const,
      render: (_: unknown, row: Row) => {
        const count = row[field];
        if (!count) return <span className="scene-set-compare-secondary">0</span>;
        return (
          <Button
            type="link" className="scene-set-compare-link"
            aria-label={`${row.name} ${label} ${count} 个场景`}
            aria-pressed={row.nodeId === selectedNodeId && verdict === selectedVerdict}
            onClick={() => onSelect(row.nodeId, verdict)}
          >{count}</Button>
        );
      },
    })),
  ];

  return (
    <Collapse
      className="scene-set-comparison"
      activeKey={open ? ['scene-set-stats'] : []}
      onChange={(keys) => setOpen(keys.includes('scene-set-stats'))}
      ghost
      items={[{
        key: 'scene-set-stats',
        label: '按场景集统计',
        children: (
          <>
            <p className="scene-set-compare-secondary scene-set-compare-note">
              每级汇总其全部下级场景并去重，每个场景取最新任务；未完成计入场景数。点击数字筛选下方明细，再次点击取消。
            </p>
            {error ? (
              <Alert
                type="error" showIcon message="场景集统计加载失败"
                action={<Button size="small" onClick={onRetry}>重试</Button>}
              />
            ) : loading || nodes == null ? (
              <div className="scene-set-compare-loading" role="status" aria-label="正在加载场景集统计"><Spin /></div>
            ) : (
              <Table<Row>
                columns={columns} dataSource={rows} rowKey="nodeId"
                size="small" pagination={false} scroll={{ x: 720 }}
                expandable={{
                  expandedRowKeys: expandedKeys,
                  onExpandedRowsChange: setExpandedKeys,
                  indentSize: 20,
                }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无场景集数据" /> }}
              />
            )}
          </>
        ),
      }]}
    />
  );
}
